import api from "@/lib/api";

/**
 * Download a school-system PDF to disk without opening a new tab.
 * Uses the axios `api` instance (so the auth token / withCredentials
 * behaviour is identical to every other API call) and streams the
 * response as a blob, then triggers a hidden <a download> click.
 * Fixes mobile-browser pop-up blocking that fires when window.open
 * runs after an awaited fetch.
 *
 * @param {string} path   Backend endpoint under /school-system,
 *                         e.g. "/school-system/students/abc/performance/pdf".
 *                         Query string may be appended by the caller.
 * @param {string} [filename]  Suggested download filename.
 */
export async function downloadPdf(path, filename) {
  const r = await api.get(path, { responseType: "blob" });
  // Prefer server-supplied filename when present.
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(cd);
  const suggested = m ? decodeURIComponent(m[1].trim()) : (filename || "document.pdf");
  const blob = new Blob([r.data], { type: r.headers?.["content-type"] || "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggested.replace(/[\r\n]/g, "").slice(0, 120);
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Cleanup — give the browser a beat to start the download.
  setTimeout(() => {
    try { document.body.removeChild(a); } catch { /* noop */ }
    URL.revokeObjectURL(url);
  }, 1000);
}


export const GRADES = [
  "Creche", "Nursery 1", "Nursery 2",
  "Kindergarten 1", "Kindergarten 2",
  "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6",
  "Grade 7", "Grade 8", "Grade 9",
  "SHS 1", "SHS 2", "SHS 3",
  "Level 100", "Level 200", "Level 300", "Level 400", "Level 500", "Level 600",
];

export const schoolSystemApi = {
  myRoles: () => api.get("/school-system/my-roles").then(r => r.data),

  // Onboarding (Tain admin)
  assignHeadteacher: (school_id, body) =>
    api.post(`/school-system/schools/${school_id}/assign-headteacher`, body).then(r => r.data),

  // Teacher assignments (headteacher)
  listAssignments: (school_id) =>
    api.get(`/school-system/teacher-assignments`, { params: { school_id } }).then(r => r.data),
  assignTeacher: (body) => api.post(`/school-system/teacher-assignments`, body).then(r => r.data),
  revokeAssignment: (id) => api.delete(`/school-system/teacher-assignments/${id}`).then(r => r.data),
  hardDeleteAssignment: (id) => api.delete(`/school-system/teacher-assignments/${id}/hard`).then(r => r.data),

  // Grading policy overrides (headteacher-only)
  getGradingPolicy: (school_id) => api.get(`/school-system/grading-policy`, { params: { school_id } }).then(r => r.data),
  saveGradingPolicy: (body) => api.put(`/school-system/grading-policy`, body).then(r => r.data),
  resetGradingPolicy: (school_id) => api.delete(`/school-system/grading-policy`, { params: { school_id } }).then(r => r.data),

  // Student promotion (headteacher-only)
  promoteStudent: (body) => api.post(`/school-system/students/promote`, body).then(r => r.data),
  promoteStudentsBulk: (body) => api.post(`/school-system/students/promote-bulk`, body).then(r => r.data),
  promoteClass: (body) => api.post(`/school-system/students/promote-class`, body).then(r => r.data),
  studentHistory: (student_id, student_id_confirm) =>
    api.get(`/school-system/students/${student_id}/history`,
            { params: student_id_confirm ? { student_id_confirm } : {} }).then(r => r.data),

  // Students
  createStudent: (body) => api.post(`/school-system/students`, body).then(r => r.data),
  listStudents: (school_id, grade) =>
    api.get(`/school-system/students`, { params: { school_id, grade } }).then(r => r.data),
  updateStudent: (student_id, body) =>
    api.patch(`/school-system/students/${student_id}`, body).then(r => r.data),
  deleteStudent: (student_id) =>
    api.delete(`/school-system/students/${student_id}`).then(r => r.data),
  parentView: (student_id, parent_email) =>
    api.get(`/school-system/parent/${student_id}`,
             parent_email ? { params: { parent_email } } : {}).then(r => r.data),

  // Subjects
  createSubject: (body) => api.post(`/school-system/subjects`, body).then(r => r.data),
  updateSubject: (id, body) => api.patch(`/school-system/subjects/${id}`, body).then(r => r.data),
  listSubjects: (school_id, grade) =>
    api.get(`/school-system/subjects`, { params: { school_id, grade } }).then(r => r.data),
  archiveSubject: (id) => api.delete(`/school-system/subjects/${id}`).then(r => r.data),

  // Records / Attendance / Behaviour / Homework / Fees
  submitRecord: (body) => api.post(`/school-system/records`, body).then(r => r.data),
  listRecords: (school_id, student_id) =>
    api.get(`/school-system/records`, { params: { school_id, student_id } }).then(r => r.data),
  markAttendance: (body) => api.post(`/school-system/attendance`, body).then(r => r.data),
  attendanceDashboard: (school_id, day) =>
    api.get(`/school-system/attendance/dashboard`,
            { params: { school_id, ...(day ? { day } : {}) } }).then(r => r.data),
  approveAllAttendanceToday: (school_id, day) =>
    api.post(`/school-system/attendance/approve-today`,
             { school_id, ...(day ? { day } : {}) }).then(r => r.data),
  submitBehaviour: (body) => api.post(`/school-system/behaviour`, body).then(r => r.data),
  createHomework: (body) => api.post(`/school-system/homework`, body).then(r => r.data),
  submitFee: (body) => api.post(`/school-system/fees`, body).then(r => r.data),

  // Lesson notes
  submitLessonNote: (body) => api.post(`/school-system/lesson-notes`, body).then(r => r.data),
  uploadLessonNoteAttachment: (school_id, grade, file) => {
    const fd = new FormData();
    fd.append("school_id", school_id);
    fd.append("grade", grade);
    fd.append("file", file);
    return api.post(`/school-system/lesson-notes/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  listLessonNotes: (school_id, grade) =>
    api.get(`/school-system/lesson-notes`, { params: { school_id, grade } }).then(r => r.data),

  // Approvals (headteacher)
  listApprovals: (school_id) =>
    api.get(`/school-system/approvals`, { params: { school_id } }).then(r => r.data),
  decideApproval: (body) => api.post(`/school-system/approvals/decide`, body).then(r => r.data),
  decideApprovalBulk: (body) => api.post(`/school-system/approvals/decide-bulk`, body).then(r => r.data),

  // Parent messages
  parentSendMessage: (body) => api.post(`/school-system/parent-messages`, body).then(r => r.data),
  listParentMessages: (school_id) =>
    api.get(`/school-system/parent-messages`, { params: { school_id } }).then(r => r.data),

  // Appointments
  requestAppointment: (body) => api.post(`/school-system/appointments`, body).then(r => r.data),
  decideAppointment: (id, body) =>
    api.post(`/school-system/appointments/${id}/decide`, body).then(r => r.data),
  listAppointments: (school_id) =>
    api.get(`/school-system/appointments`, { params: { school_id } }).then(r => r.data),
  parentAppointments: (student_id) =>
    api.get(`/school-system/parent-appointments/${student_id}`).then(r => r.data),

  // Report card
  reportCard: (school_id, student_id, academic_year, term, student_id_confirm) =>
    api.get(`/school-system/report-card`, {
      params: { school_id, student_id, academic_year, term,
                ...(student_id_confirm ? { student_id_confirm } : {}) }
    }).then(r => r.data),

  // Publish report cards (headteacher)
  publishReportCards: (body) =>
    api.post(`/school-system/publish-report-cards`, body).then(r => r.data),
  listPublishedReportCards: (school_id, params = {}) =>
    api.get(`/school-system/published-report-cards`, { params: { school_id, ...params } }).then(r => r.data),
  reportCardPdfUrl: (school_id, student_id, academic_year, term, student_id_confirm) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const qp = new URLSearchParams({ school_id, student_id, academic_year, term });
    if (student_id_confirm) qp.set("student_id_confirm", student_id_confirm);
    return `${backend}/api/school-system/report-card/pdf?${qp.toString()}`;
  },

  // ---- Phase 3c ----
  // Admission register (HT)
  admissionRegister: (school_id, params = {}) =>
    api.get(`/school-system/admission-register`, { params: { school_id, ...params } }).then(r => r.data),

  // School calendar (reopening / closing dates)
  getCalendar: (school_id, academic_year) =>
    api.get(`/school-system/calendar`, { params: { school_id, ...(academic_year ? { academic_year } : {}) } }).then(r => r.data),
  saveCalendar: (body) =>
    api.put(`/school-system/calendar`, body).then(r => r.data),

  // Master fees templates
  listFeeTemplates: (school_id, params = {}) =>
    api.get(`/school-system/fee-templates`, { params: { school_id, ...params } }).then(r => r.data),
  createFeeTemplate: (body) => api.post(`/school-system/fee-templates`, body).then(r => r.data),
  deleteFeeTemplate: (id) => api.delete(`/school-system/fee-templates/${id}`).then(r => r.data),
  applyFeeTemplate: (id) => api.post(`/school-system/fee-templates/${id}/apply`).then(r => r.data),

  // HT direct fee entry (with arrears carry-forward + auto balance calc)
  htUpsertFee: (body) =>
    api.post(`/school-system/headteacher-portal/fees/upsert`, body).then(r => r.data),
  htFeesByClass: (school_id, params = {}) =>
    api.get(`/school-system/headteacher-portal/fees/by-class`,
            { params: { school_id, ...params } }).then(r => r.data),
  // Read-only for teachers & parents (iter 113) — HT is the sole author.
  feesByClass: (school_id, grade, params = {}) =>
    api.get(`/school-system/fees/by-class`,
            { params: { school_id, grade, ...params } }).then(r => r.data),
  feesForStudent: (student_id, params = {}) =>
    api.get(`/school-system/fees/student/${student_id}`,
            { params }).then(r => r.data),
  studentStatementPdfPath: (student_id, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return `/school-system/fees/student/${student_id}/statement/pdf${q ? `?${q}` : ""}`;
  },
  feeReceiptPdfPath: (fee_id, receipt_id) =>
    `/school-system/fees/${fee_id}/receipt/pdf${receipt_id ? `?receipt_id=${receipt_id}` : ""}`,
  htFeesForStudent: (student_id, params = {}) =>
    api.get(`/school-system/headteacher-portal/fees/student/${student_id}`,
            { params }).then(r => r.data),
  htFeeRemindPreview: (school_id, params = {}) =>
    api.get(`/school-system/headteacher-portal/fees/remind/preview`,
            { params: { school_id, ...params } }).then(r => r.data),
  htSendFeeReminder: (body) =>
    api.post(`/school-system/headteacher-portal/fees/remind`, body).then(r => r.data),
  htListFeeReminders: (school_id, limit = 25) =>
    api.get(`/school-system/headteacher-portal/fees/reminders`,
            { params: { school_id, limit } }).then(r => r.data),
  htApprovalsHistory: (school_id, params = {}) =>
    api.get(`/school-system/headteacher-portal/approvals/history`,
            { params: { school_id, ...params } }).then(r => r.data),
  teacherApprovalsHistory: (school_id, params = {}) =>
    api.get(`/school-system/teacher-portal/approvals/history`,
            { params: { school_id, ...params } }).then(r => r.data),
  attendancePdfPath: (school_id, date, grade) => {
    const qp = new URLSearchParams({ school_id, date });
    if (grade) qp.set("grade", grade);
    return `/school-system/attendance/pdf?${qp.toString()}`;
  },
  aiGenerateHomework: (body) =>
    api.post(`/school-system/homework/ai-generate`, body).then(r => r.data),

  // ---- Homework: list, PDF, marking scheme, scores (Feb 2026 · iter 107) ----
  listHomework: (school_id, grade) =>
    api.get(`/school-system/homework`, { params: { school_id, grade } }).then(r => r.data),
  homeworkPdfPath: (homework_id) =>
    `/school-system/homework/${homework_id}/pdf`,
  homeworkGenerateScheme: (homework_id) =>
    api.post(`/school-system/homework/${homework_id}/marking-scheme/ai-generate`).then(r => r.data),
  homeworkSubmitScheme: (scheme_id) =>
    api.post(`/school-system/homework/marking-scheme/submit`, { scheme_id }).then(r => r.data),
  homeworkGetScheme: (homework_id) =>
    api.get(`/school-system/homework/${homework_id}/marking-scheme`).then(r => r.data),
  homeworkSchemePdfPath: (homework_id) =>
    `/school-system/homework/${homework_id}/marking-scheme/pdf`,
  homeworkSubmitScores: (homework_id, scores) =>
    api.post(`/school-system/homework/${homework_id}/scores`, { scores }).then(r => r.data),
  homeworkListScores: (homework_id) =>
    api.get(`/school-system/homework/${homework_id}/scores`).then(r => r.data),
  homeworkScoresPdfPath: (homework_id) =>
    `/school-system/homework/${homework_id}/scores/pdf`,
  parentHomeworkScores: (student_id) =>
    api.get(`/school-system/parent/${student_id}/homework-scores`).then(r => r.data),

  // ---- Cross-school leaderboard + school branding ----
  leaderboard: (limit = 20) =>
    api.get(`/school-system/leaderboard`, { params: { limit } }).then(r => r.data),
  myLeaderboardPosition: (params = {}) =>
    api.get(`/school-system/leaderboard/my-position`, { params }).then(r => r.data),
  schoolBranding: (school_id) =>
    api.get(`/school-system/schools/${school_id}/branding`).then(r => r.data),
  myTeacherAssignments: (school_id) =>
    api.get(`/school-system/teacher-portal/my-assignments`,
            { params: school_id ? { school_id } : {} }).then(r => r.data),
  htBroadcastParents: (body) =>
    api.post(`/school-system/headteacher-portal/parents/broadcast`, body).then(r => r.data),
  htListBroadcasts: (school_id, limit = 25) =>
    api.get(`/school-system/headteacher-portal/parents/broadcasts`,
            { params: { school_id, limit } }).then(r => r.data),

  // Receipts
  issueReceipt: (fee_id, body) =>
    api.post(`/school-system/fees/${fee_id}/receipt`, body).then(r => r.data),
  listReceipts: (fee_id) =>
    api.get(`/school-system/fees/${fee_id}/receipts`).then(r => r.data),

  // Teacher entries (own pending / returned entries)
  listTeacherEntries: (school_id, kind) =>
    api.get(`/school-system/teacher-entries`, { params: { school_id, ...(kind ? { kind } : {}) } }).then(r => r.data),
  deleteTeacherEntry: (approval_id) =>
    api.delete(`/school-system/teacher-entries/${approval_id}`).then(r => r.data),

  // Enhanced performance (ranked + graded + remarked)
  performanceRanked: (student_id, params = {}) =>
    api.get(`/school-system/students/${student_id}/performance-ranked`, { params }).then(r => r.data),

  // Parent-facing AI content
  parentLessonNotes: (student_id) =>
    api.get(`/school-system/parent/${student_id}/lesson-notes`).then(r => r.data),
  parentTests: (student_id) =>
    api.get(`/school-system/parent/${student_id}/tests`).then(r => r.data),
  parentMarkingSchemes: (student_id) =>
    api.get(`/school-system/parent/${student_id}/marking-schemes`).then(r => r.data),

  // Parent portal access status (mirror of HT portal charge status)
  parentAccessStatus: (student_id) =>
    api.get(`/school-system/parent/${student_id}/access-status`).then(r => r.data),

  // ---- AI generation (Phase 3a/3b — teacher tools) ----
  aiGenerateLessonNote: (body) =>
    api.post(`/school-system/lesson-notes/ai-generate`, body).then(r => r.data),
  saveGeneratedLessonNote: (body) =>
    api.post(`/school-system/lesson-notes`, body).then(r => r.data),  // reuses existing manual submit
  listLessonNotes: (school_id, params = {}) =>
    api.get(`/school-system/lesson-notes`, { params: { school_id, ...params } }).then(r => r.data),
  listMyLessonNotes: (school_id, params = {}) =>
    api.get(`/school-system/lesson-notes/mine`, { params: { school_id, ...params } }).then(r => r.data),
  batchSubmitLessonNotes: (note_ids) =>
    api.post(`/school-system/lesson-notes/batch-submit`, { note_ids }).then(r => r.data),

  aiGenerateTest: (body) =>
    api.post(`/school-system/tests/ai-generate`, body).then(r => r.data),
  submitTest: (test_id) =>
    api.post(`/school-system/tests/${test_id}/submit`).then(r => r.data),
  aiGenerateMarkingScheme: (test_id) =>
    api.post(`/school-system/tests/${test_id}/marking-scheme/ai-generate`).then(r => r.data),
  listMarkingSchemes: (school_id, params = {}) =>
    api.get(`/school-system/marking-schemes`, { params: { school_id, ...params } }).then(r => r.data),
  submitMarkingScheme: (scheme_id) =>
    api.post(`/school-system/marking-schemes/${scheme_id}/submit`).then(r => r.data),
  listTests: (school_id, params = {}) =>
    api.get(`/school-system/tests`, { params: { school_id, ...params } }).then(r => r.data),

  // ---- Student Results (Teacher score entry) ----
  submitStudentResult: (body) =>
    api.post(`/school-system/student-results`, body).then(r => r.data),
  listStudentResults: (school_id, params = {}) =>
    api.get(`/school-system/student-results`, { params: { school_id, ...params } }).then(r => r.data),
  approveStudentResult: (result_id) =>
    api.post(`/school-system/student-results/${result_id}/approve`).then(r => r.data),
  returnStudentResult: (result_id, reason) =>
    api.post(`/school-system/student-results/${result_id}/return`, { reason }).then(r => r.data),
  voidStudentResult: (result_id) =>
    api.post(`/school-system/student-results/${result_id}/void`).then(r => r.data),

  // Students in a class (teacher score-entry needs the roster)
  listStudentsForGrade: (school_id, grade) =>
    api.get(`/school-system/students`, { params: { school_id, grade } }).then(r => r.data),

  // Publish Performance (HT-only)
  publishPerformance: (body) =>
    api.post(`/school-system/performance/publish`, body).then(r => r.data),
  listPerformancePublications: (school_id, student_id) =>
    api.get(`/school-system/performance/publications`, {
      params: { school_id, ...(student_id ? { student_id } : {}) },
    }).then(r => r.data),

  // School logo upload (HT-only)
  uploadSchoolLogo: (school_id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/school-system/schools/${school_id}/logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },
  schoolLogoUrl: (school_id) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/school-system/schools/${school_id}/logo`,
};
